import axios from 'axios';
import store from '../../store';
import {exportEnabled} from '../export';
import {NoOperation} from '../utils';
import {WEBSOCKET_ACTION_AUTHORISE, WEBSOCKET_ACTION_PING, WEBSOCKET_ACTION_WELCOME} from './actions';

let socketTries = 0;

class PortunaWebSocketManager
{
    listeners: Record<string, WebsocketCallback[]> = {};

    hasListener(action: string)
    {
        return 'undefined' !== typeof this.listeners[action];
    }

    getListener(action: string)
    {
        return this.listeners[action] || [];
    }

    listen(action: string, callback: WebsocketCallback)
    {
        if (!this.hasListener(action))
        {
            this.listeners[action] = [];
        }

        this.listeners[action].push(callback);

        return this;
    }

    remove(action: string, callback: WebsocketCallback)
    {
        if ('undefined' === typeof this.listeners[action])
        {
            return this;
        }

        for (let index in this.listeners[action])
        {
            if (!this.listeners[action].hasOwnProperty(index))
            {
                continue;
            }

            if (this.listeners[action][index] === callback)
            {
                this.listeners[action].splice(parseInt(index), 1);
            }
        }

        return this;
    }
}

class PortunaWebSocket
{
    onOpen = false;

    isOpen = false;

    socket: WebSocket;

    host: string;

    pingInterval: Nullable<number> = null;

    manager: PortunaWebSocketManager = new PortunaWebSocketManager();

    restart = true;

    tryingRestart = false;

    onCreatePromise: Nullable<Promise<void>> = null;

    private create(): Promise<void>
    {
        if (this.onCreatePromise)
        {
            return this.onCreatePromise;
        }

        const promise: Promise<void> = new Promise((resolve, reject) =>
        {
            let handled  = false;
            this.restart = true;

            const src = this.getWebsocketUrl();
            if (this.onOpen || this.isOpen || exportEnabled() || (!src && !this.host))
            {
                console.debug('[WebSocket]: cancel create', this.isOpen, exportEnabled(), src);
                return;
            }

            this.onOpen = true;

            // src is only filled @ init - not on second try
            if (src)
            {
                this.host = src;
            }

            this.socket = new WebSocket('wss://' + this.host);

            this.socket.onclose = (e) =>
            {
                this.isOpen          = false;
                this.onOpen          = false;
                this.onCreatePromise = null;

                this.removePing();
                this.socket.onclose = () => {};

                if (!e.wasClean)
                {
                    this.create().finally(NoOperation);
                }
            };

            this.socket.onmessage = (e) =>
            {
                if (e.data)
                {
                    try
                    {
                        this.handleMessage(JSON.parse(e.data));
                    }
                    catch (e)
                    {
                        console.error('[WebSocket]: can\'t handle message', e);
                    }
                }
            };

            this.socket.onerror = (e) =>
            {
                console.error('[WebSocket]: onerror', e);

                this.isOpen          = false;
                this.onOpen          = false;
                this.onCreatePromise = null;

                if (!handled && !this.tryingRestart)
                {
                    window.setTimeout(() =>
                    {
                        this.dirtyRestart()
                        .then(resolve).catch(reject);
                    }, Math.floor(Math.random() * 1000));
                }
                else if (!handled)
                {
                    reject();
                }
            };

            this.socket.onopen = (e) =>
            {
                this.isOpen          = true;
                this.onOpen          = false;
                this.onCreatePromise = null;

                socketTries = 0;
                handled     = true;
                resolve();

                const user = store.state.layoutData.user;
                this.send(
                    WEBSOCKET_ACTION_AUTHORISE,
                    {
                        user: user.id,
                        token: user.chatToken,
                    },
                );
            };
        });

        this.onCreatePromise = promise;

        return promise;
    }

    getWebsocketUrl()
    {
        if(store.state.layoutData.websocket_url)
        {
            return store.state.layoutData.websocket_url;
        }

        const Url = new URL(document.currentScript instanceof HTMLScriptElement ? document.currentScript.src : '');

        return Url.hostname + '/websocket';
    }

    close()
    {
        this.restart = false;
        this.socket.close();
    }

    private _try(reject: VoidFunction, resolve: VoidFunction)
    {
        this.tryingRestart = true;
        socketTries++;

        const errorHandler = () =>
        {
            if (!this.restart || this.tryingRestart || socketTries >= 3)
            {
                reject();
                return;
            }
            else
            {
                this._try(resolve, reject);
            }
        };

        axios.get('/application/ajax/create-websocket').then(() =>
        {
            this.create()
            .then(() =>
            {
                resolve();
            })
            .catch(() =>
            {
                window.setTimeout(errorHandler, 3_000);
            }).finally(() =>
            {
                this.tryingRestart = false;
            });
        })
        .catch(() =>
        {
            window.setTimeout(errorHandler, 1_000);
        }).finally(() =>
        {
            this.tryingRestart = false;
        });
    }

    dirtyRestart(): Promise<void>
    {
        return new Promise((resolve, reject) =>
        {
            this._try(reject, resolve);
        });
    }

    private removePing()
    {
        if (this.pingInterval)
        {
            window.clearInterval(this.pingInterval);
        }
    }

    private addPing()
    {
        this.removePing();

        this.pingInterval = window.setInterval(() =>
        {
            this.send(WEBSOCKET_ACTION_PING);
        }, 30_000);
    }

    private iAmWelcome()
    {
        this.addPing();
    }

    /**
     * currently private 'cause we dont send over index
     * if you want change it: please ask first.
     * and think about: Why? Ajax seems better option.
     *
     * @private
     */
    private send(action: string, payload: object = {})
    {
        this.socket.send(JSON.stringify({
            action,
            payload,
        }));
    }

    private handleMessage({
        action,
        payload,
    }: { action: string, payload: WebsocketCallbackPayload })
    {
        // internally handle
        if (WEBSOCKET_ACTION_WELCOME === action)
        {
            this.iAmWelcome();
            return;
        }
        else if (WEBSOCKET_ACTION_PING === action)
        {
            // nothing to do.
            return;
        }

        // no one listen. do nothing.
        if (!this.manager.hasListener(action))
        {
            return;
        }

        try
        {
            for (let callback of this.manager.getListener(action))
            {
                callback(payload);
            }
        }
        catch (e)
        {
            console.error('[WebSocket]: can\'t handle payload', payload, e);
        }
    }

    getManager(): Promise<PortunaWebSocketManager>
    {
        return new Promise((resolve, reject) =>
        {
            if (this.isOpen)
            {
                resolve(this.manager);
            }
            else
            {
                this.create().then(() =>
                {
                    resolve(this.manager);
                }).catch(reject);
            }
        });
    }
}

const websockets = new PortunaWebSocket();

export default websockets;